import { useState } from "react";
import {
  Box,
  css,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  useTheme,
} from "@mui/material";
import { Input } from "../bits/inputs";
import { PrimaryButton } from "../bits/buttons";
import { z } from "zod";
import { useDropzone } from "react-dropzone";
import type { Publication } from "@rowan/prisma";
import { DropdownContainer } from "../bits/inputs/DropdownContainer";
import { useConfig } from "../hooks/useConfig";

const publicationSchema = z.object({
  title: z.string().min(1, "Name is required"),
  status: z
    .enum(["DRAFT", "PUBLISHED", "CREATING", "UPDATING", "ERROR"])
    .optional(),
  files: z.array(z.instanceof(File)).optional(),
  images: z.array(z.instanceof(File)).optional(),
});

export type PublicationFormData = z.infer<typeof publicationSchema>;

type PublicationErrors = {
  title?: string;
  files?: string;
};

type AddPublicationFormProps = {
  publication?: Publication;
  onSubmit: (data: PublicationFormData) => void;
  error?: string;
};

export const AddPublicationForm = ({
  onSubmit,
  error,
  publication,
}: AddPublicationFormProps) => {
  const { assetBucketUrl } = useConfig();

  const [formData, setFormData] = useState<PublicationFormData>({
    title: publication?.title ?? "",
    status: publication?.status,
    files: [],
  });
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (incomingFiles) => {
      setFormData((prev) => ({
        ...prev,
        files: incomingFiles,
      }));
    },
    accept: {
      "text/html": [".html", ".htm"],
    },
    maxFiles: 1,
  });

  const {
    getRootProps: getRouteImageProps,
    getInputProps: getInputImageProps,
  } = useDropzone({
    onDrop: (incomingImages) => {
      setFormData((prev) => ({
        ...prev,
        images: incomingImages,
      }));
    },
    accept: {
      "image/*": [".png", ".jpg", ".jpeg"],
    },
    maxFiles: 1,
  });

  const [errors, setErrors] = useState<PublicationErrors>({});
  const theme = useTheme();
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    const result = publicationSchema.safeParse(formData);
    if (!result.success) {
      setErrors(
        result.error.errors.reduce((acc, error) => {
          const field = error.path[0] as keyof typeof errors;
          acc[field] = error.message;
          return acc;
        }, {} as PublicationErrors),
      );
      return;
    }
    onSubmit(formData);
  };
  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
    >
      {publication?.filename && (
        <a href={`/pub/${publication.filename}`}>Open publication</a>
      )}
      <Input
        label="Title"
        name="title"
        value={formData.title}
        onChange={handleChange}
        error={!!errors.title}
        helperText={errors.title}
        required
        disabled={publication && publication.status === "UPDATING"}
      />
      {publication &&
        publication.status !== "UPDATING" &&
        !formData.files?.length && (
          <DropdownContainer {...getRootProps()}>
            <input {...getInputProps()} />
            <p>
              Drag and drop or click here to upload the html for this
              publication
            </p>
          </DropdownContainer>
        )}
      {!!formData.files?.length && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 8px;
          `}
        >
          <p>Selected file:</p>
          {formData.files?.map((file) => (
            <div
              key={file.name}
              css={css`
                display: flex;
                justify-content: space-between;
                width: 100%;
              `}
            >
              <span>{file.name}</span>
              <span>{Math.round(file.size / 1024)} KB</span>
              <PrimaryButton
                onClick={() => {
                  setFormData((prev) => ({
                    ...prev,
                    files: [],
                  }));
                }}
              >
                Delete
              </PrimaryButton>
            </div>
          ))}
        </div>
      )}
      {/* Image */}
      {publication?.imageUrl && (
        <img
          src={`${assetBucketUrl ?? ""}/${publication.imageUrl}`}
          alt="Publication"
          css={css`
            object-fit: contain;
            height: 240px;
            width: 160px;
          `}
        />
      )}
      {publication &&
        ["DRAFT", "PUBLISHED"].includes(publication.status) &&
        !formData.images?.length && (
          <DropdownContainer {...getRouteImageProps()}>
            <input {...getInputImageProps()} />
            <p>
              Drag and drop or click here to upload the image for this
              publication
            </p>
          </DropdownContainer>
        )}
      {publication && ["DRAFT", "PUBLISHED"].includes(publication.status) && (
        <>
          <FormLabel>Status</FormLabel>
          <RadioGroup
            name="status"
            value={formData.status}
            onChange={handleChange}
          >
            <FormControlLabel value="DRAFT" control={<Radio />} label="Draft" />
            <FormControlLabel
              value="PUBLISHED"
              control={<Radio />}
              label="Published"
            />
          </RadioGroup>
        </>
      )}
      {!!formData.images?.length && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 8px;
          `}
        >
          <p>Selected image:</p>
          {formData.images?.map((image) => (
            <div
              key={image.name}
              css={css`
                display: flex;
                justify-content: space-between;
                width: 100%;
              `}
            >
              <span>{image.name}</span>
              <span>{Math.round(image.size / 1024)} KB</span>
              <PrimaryButton
                onClick={() => {
                  setFormData((prev) => ({
                    ...prev,
                    images: [],
                  }));
                }}
              >
                Delete
              </PrimaryButton>
            </div>
          ))}
        </div>
      )}
      <PrimaryButton
        type="submit"
        disabled={publication && publication.status === "UPDATING"}
      >
        {publication ? "Update Publication" : "Add Publication"}
      </PrimaryButton>
      <p style={{ color: theme.palette.error.main }}>{error}</p>
    </Box>
  );
};
